import useTranslation from 'next-translate/useTranslation'

import { ComparablesInfoBoxProps } from '../comparables-info'
import { Button } from '@unreserved-frontend-v2/ui/button/button'
import { FlexRow } from '@unreserved-frontend-v2/ui/flex/flex-row'
import { TagIcon } from '@unreserved-frontend-v2/ui/icons/tag'
import { DEFAULT_LOCALE } from '@unreserved-frontend-v2/utils/locale'
import { currency } from '@unreserved-frontend-v2/utils/currency'
import { buildQueryStringFromFilters } from '../../../../utils/routing/build-query-string-from-filters'
import { DEFAULT_LISTING_DIRECTORY, DEFAULT_PRIMARY_FILTER_SELECTIONS } from '../../../../utils/routing/constants'

export type MedianListPriceInfoProps = Pick<ComparablesInfoBoxProps, 'data' | 'filterSelections'> & {
  isViewingSold: boolean
  displayedFilters: string
  placeTitle: string
}

export function MedianListPriceInfo({
  data,
  isViewingSold,
  displayedFilters,
  placeTitle,
  filterSelections,
}: MedianListPriceInfoProps) {
  const { t } = useTranslation()

  if (!data?.totalListingsCount) {
    return null
  }

  const title = !isViewingSold
    ? t(`listings:comparables-info.title-for-sale-homes`, {
        homes: t(`listings:comparables-info.number-home-for-sale`, {
          count: data?.totalListingsCount,
          number: data?.totalListingsCount?.toLocaleString(DEFAULT_LOCALE),
        }),
        place: placeTitle,
        havingFilters: displayedFilters
          ? t('listings:comparables-info.having-displayed-filters', {
              displayedFilters,
            })
          : '',
      })
    : t(`listings:comparables-info.sub-title-for-sale`, {
        homes: t(`listings:comparables-info.similar-homes-for-sale`, {
          count: data?.totalListingsCount,
          number: data?.totalListingsCount?.toLocaleString(DEFAULT_LOCALE),
        }),
      })

  const diffPercent = Math.round((data?.listingPrice / data?.listingPriceYearAgo - 1) * 10000) / 100

  return (
    <>
      <FlexRow className="justify-between pt-2.5 font-bold">
        {t('listings:comparables-info.median-for-sale-price')}
        <TagIcon className="flex-shrink-0" />
      </FlexRow>
      <hr className="text-shades-200 mt-1.5 mb-2" />
      <p
        className="text-sm leading-5"
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <div className="pt-1.5 text-base leading-7" data-testid="sale-price-info">
        <div className="text-primary text-xl font-bold">{currency(Math.round(data?.listingPrice))}</div>
      </div>
      {diffPercent && diffPercent !== Infinity ? (
        <p
          dangerouslySetInnerHTML={{
            __html: t(`listings:comparables-info.${diffPercent > 0 ? 'increase' : 'decrease'}-of-percentage`, {
              percentage: Math.abs(diffPercent),
            }),
          }}
          className="text-sm"
        />
      ) : null}
      {isViewingSold ? (
        <Button
          href={buildQueryStringFromFilters(
            {
              ...filterSelections,
              listingStatuses: DEFAULT_PRIMARY_FILTER_SELECTIONS.listingStatuses,
            },
            `/${DEFAULT_LISTING_DIRECTORY}/[listing]`
          )}
          className="mt-2"
        >
          {t(`listings:comparables-info.sold-view-homes-cta`)}
        </Button>
      ) : null}
    </>
  )
}

export default MedianListPriceInfo
